/**
 * sl-728o.6.2: Onboarding Session Registration Hook
 *
 * Registers the onboarding-demo-tour session with 10 action-driven steps
 * in the TutorialRegistry. Called from workspace-layout.tsx WelcomeScreenWrapper.
 *
 * Opens the tutorial document only when entering its step, or on completion.
 */
import { AppSidebarService } from '@affine/core/modules/app-sidebar';
import { DocsService } from '@affine/core/modules/doc';
import { CMDKQuickSearchService } from '@affine/core/modules/quicksearch/services/cmdk';
import { WorkbenchService } from '@affine/core/modules/workbench';
import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import { useCallback, useEffect } from 'react';

import { TutorialRegistry } from '../../services/tutorial-registry';
import { createDemoTourSession } from './demo-tour';

export function useRegisterOnboardingSession(): void {
  const registry = useService(TutorialRegistry);
  const docsService = useService(DocsService);
  const appSidebar = useService(AppSidebarService).sidebar;
  const quickSearch = useService(CMDKQuickSearchService);
  const workbench = useService(WorkbenchService).workbench;
  const t = useI18n();

  const getDemoDocId = useCallback(
    () => docsService.list.demoDocs$.value.find(doc => !doc.trash$.value)?.id,
    [docsService]
  );

  const openDemoDoc = useCallback(
    async (docId: string, signal?: AbortSignal) => {
      signal?.throwIfAborted();
      workbench.openDoc({ docId, mode: 'page' });
      const { doc, release } = docsService.open(docId);
      try {
        await doc.waitForSyncReady();
        signal?.throwIfAborted();
      } finally {
        release();
      }
    },
    [docsService, workbench]
  );

  const handleTourComplete = useCallback(() => {
    // Navigate to the permission-filtered onboarding doc after the tour.
    const tutorialDoc = docsService.list.demoDocs$.value.find(
      doc => !doc.trash$.value
    );
    if (tutorialDoc) {
      workbench.openDoc(tutorialDoc.id);
    }
  }, [docsService, workbench]);

  useEffect(() => {
    const session = createDemoTourSession(t, {
      closeQuickSearch: () => quickSearch.close(),
      getDemoDocId,
      openDemoDoc,
      onTourComplete: handleTourComplete,
      setSidebarOpen: open => appSidebar.setOpen(open),
    });
    registry.register(session);
    return () => registry.unregister(session.id);
  }, [
    appSidebar,
    getDemoDocId,
    handleTourComplete,
    openDemoDoc,
    quickSearch,
    registry,
    t,
  ]);
}
